<template>
    <b-container fluid>
        <!-- Main Row -->
        <b-row class="mt-5 justify-content-md-center px-3 px-sm-5">
            <!-- Logo Section -->
            <b-col cols="12" class="d-flex justify-content-center align-items-center mb-4">
                <div class="logo-wrapper">
                    <img src="../../../assets/img/island-candos-logo.png" width="100%" />
                </div>
            </b-col>

            <!-- Title Section -->
            <b-col cols="12" class="d-flex justify-content-center align-items-center mb-4">
                <h2 class="text-center">{{ $t('room_reservation_system') }}</h2>
            </b-col>

                <!-- Filter Form -->
                <b-col cols="12" xl="6" class="mx-auto">
                    <b-alert
                        v-if="showMessageAlert"
                        show
                        variant="danger"
                        class="custom-alert mb-4 mx-auto"
                        dismissible
                        @dismissed="showMessageAlert = false"
                    >
                        <div v-html="$t('housing_reservation_form_30_days_error')"></div>
                    </b-alert>
                    <datatable-filter v-show="datatable.filterStatus" @filter="filter" @filterClear="filterClear">
                        <ValidationObserver ref="formModalValidate">
                            <!-- Date Range Filter -->
                            <b-col cols="12">
                                <b-row>
                                    <b-col cols="6">
                                        <ValidationProvider name="start_date" rules="required" v-slot="{ valid, errors }">
                                            <b-form-group :label="$t('start_date')">
                                                <select-date v-model="datatable.queryParams.filter.check_in_date"
                                                             :validation-error="errors[0]"></select-date>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </b-col>

                                    <b-col cols="6">
                                        <ValidationProvider name="end_date" rules="required" v-slot="{ valid, errors }">
                                            <b-form-group :label="$t('end_date')">
                                                <select-date v-model="datatable.queryParams.filter.check_out_date" :validation-error="errors[0]"></select-date>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </b-col>
                                </b-row>
                            </b-col>

                            <!-- Gender Filter -->
                            <b-col cols="12">
                                <ValidationProvider name="gender" rules="required" v-slot="{ valid, errors }">
                                    <b-form-group :label="$t('gender')">
                                        <gender-selectbox
                                            v-model="datatable.queryParams.filter.gender"
                                            :show-other="false"
                                            :validate-error="errors[0]"
                                        />
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Room Type Filter -->
                            <b-col cols="12">
                                <ValidationProvider name="room_type" rules="" v-slot="{ valid, errors }">
                                    <b-form-group :label="$t('room_type')">
                                        <parameter-selectbox
                                            :public="true"
                                            code="housing_room_types"
                                            v-model="datatable.queryParams.filter.room_type"
                                            :validate-error="errors[0]"
                                        />
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <b-col cols="12">
                                <ValidationProvider name="type_status" rules="required" v-slot="{ errors }">
                                    <b-form-group :label="$t('are_you_a_bau_student_or_staff')">
                                        <b-input-group class="mb-3 flex-nowrap">
                                            <YesNoSelectbox class="w-100" v-model="type_status" />
                                        </b-input-group>
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <b-col cols="12" v-if="type_status === 'Yes'">
                                <ValidationProvider name="type" rules="required" v-slot="{ errors }">
                                    <b-form-group :label="$t('type')">
                                        <parameter-selectbox
                                            :public="true"
                                            class="w-100"
                                            v-model="requestForm.bau_type"
                                            code="housing_tenant_types"
                                            :validate-error="errors[0]"
                                        />
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <div v-if="['bahcesehir_exchange', 'besl'].includes(requestForm.bau_type) && type_status === 'Yes'">
                                <b-col cols="12">
                                    <ValidationProvider
                                        name="student_number"
                                        :rules="['bahcesehir_exchange', 'besl'].includes(requestForm.bau_type) && type_status === 'Yes' ? 'required' : ''"
                                        v-slot="{ valid, errors }"
                                    >
                                        <b-form-group :label="$t('student_number')">
                                            <b-input-group class="mb-3">
                                                <b-form-input
                                                    v-model="requestForm.student_number"
                                                    :class="errors[0] ? 'is-invalid' : ''"
                                                />
                                            </b-input-group>
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col cols="12">
                                    <ValidationProvider
                                        name="student_email"
                                        :rules="['bahcesehir_exchange', 'besl'].includes(requestForm.bau_type) && type_status === 'Yes' ? 'required|email' : ''"
                                        v-slot="{ valid, errors }"
                                    >
                                        <b-form-group :label="$t('student_email')">
                                            <b-input-group class="mb-3">
                                                <b-form-input
                                                    v-model="requestForm.student_email"
                                                    :class="errors[0] ? 'is-invalid' : ''"
                                                />
                                            </b-input-group>
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                            </div>
                        </ValidationObserver>
                    </datatable-filter>
                </b-col>

                <!-- Datatable -->
                <b-col cols="12">
                    <datatable
                        :isLoading.sync="datatable.isLoading"
                        :columns="datatable.columns"
                        :rows="datatable.rows"
                        :total="datatable.total"
                        :queryParams="datatable.queryParams"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        v-show="datatable.showTable"
                        class="datatable-responsive"
                    />
                </b-col>
        </b-row>

        <!-- Modal -->
        <CommonModal ref="updateFormModal" size="lg" :onHideOnlyX="true" @bHideModalHeaderClose="formClear">
            <template v-slot:CommonModalTitle>
                {{ $t('create_reservation_request').toUpperCase() }}
            </template>
            <template v-slot:CommonModalContent>
                <verify-email
                    v-if="formProcess == 'reservationModal'"
                    @verificationSuccess="handleVerificationSuccess"
                    @emailUpdated="reservationEmail = $event"
                />
            </template>
        </CommonModal>
    </b-container>
</template>



<script>
    // Component
    import Datatable from '@/components/datatable/Datatable'
    import DatatableFilter from '@/components/datatable/DatatableFilter'
    import GenderSelectbox from "@/components/interactive-fields/GenderSelectbox.vue";
    import CommonModal from '@/components/elements/CommonModal'

    // Services
    import HousingReservationFormService from '@/services/HousingReservationFormService'

    // Pages
    import VerifyEmail from '@/modules/housingReservationForm/pages/VerifyEmail.vue'

    // Other
    import {ValidationObserver, ValidationProvider} from 'vee-validate'
    import qs from 'qs'
    import {setGoReservationSystemForm} from "@/modules/housingReservationForm/router/middleware";
    import YesNoSelectbox from "@/components/interactive-fields/YesNoSelectbox.vue";
    import moment from "moment";


    export default {
        props: {
            rsRefresh: {
                type: Boolean,
                default: false
            }
        },
        components: {
            YesNoSelectbox,
            ValidationProvider,
            ValidationObserver,
            Datatable,
            DatatableFilter,
            GenderSelectbox,
            CommonModal,
            VerifyEmail,
        },
        metaInfo() {
            return {
                title: this.$t('room_reservation_system')
            }
        },
        data() {
            return {
                type_status:null,
                requestForm:{
                    bau_type:null,
                    student_number:null,
                    student_email:null,
                },

                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            sortable: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('create_reservation_request'),
                                    class: 'ri-calendar-check-line',
                                    hidden: false,
                                    callback: (row) => {
                                        this.updateFormShow(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.$t('building'),
                            field: 'housing_building_name',
                            sortable: true
                        },
                        {
                            label: this.$t('room'),
                            field: 'housing_room_name',
                            sortable: true
                        },
                        {
                            label: this.$t('floor'),
                            field: 'housing_floor',
                            sortable: true
                        },
                        {
                            label: this.$t('room_type'),
                            field: 'room_type_name',
                            sortable: true
                        },
                        {
                            label: this.$t('bed_type'),
                            field: 'type_name',
                            sortable: true
                        },
                        {
                            label: this.$t('bed'),
                            field: 'bed_name',
                            sortable: true
                        },
                        {
                            label: this.$t('monthly_fee'),
                            field: 'fee',
                            sortable: true,
                            formatFn: (row) => {
                               return  this.currencyFormat(row)
                            }
                        },
                        {
                            label: this.$t('total_amount'),
                            field: 'total_amount',
                            sortable: true,
                            formatFn: (row) => {
                               return  this.currencyFormat(row)
                            }
                        },
                    ],
                    rows: [],
                    total: 0,
                    filterStatus: true,
                    showTable: false,
                    queryParams: {
                        filter: {
                            check_in_date: null,
                            check_out_date: null,
                            gender: null
                        },
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                },
                formLoading: false,
                formId: null,
                formProcess: null,
                reservationEmail: '',
                selectedReservation: {},
                showMessageAlert:false
            }
        },
        watch: {
            rsRefresh: {
                handler: function(val) {
                    if(val){
                        this.getRows()
                    }
                }
            },
            'datatable.queryParams.filter.check_in_date': function(val) {
                if(this.datatable.rows.length){
                    this.datatable.showTable=false;
                    this.getRows();
                }
            },
            'datatable.queryParams.filter.check_out_date': function(val) {
                if(this.datatable.rows.length){
                    this.datatable.showTable=false;
                    this.getRows();
                }
            },
            'requestForm.bau_type': function(val) {
                if(this.datatable.rows.length){
                    this.datatable.showTable=false;
                    this.getRows();
                }
            },
            type_status: function(val) {
                if(this.datatable.rows.length){
                    this.datatable.showTable=false;
                    this.getRows();
                }
            },
        },
        methods: {
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {
                    check_in_date: null,
                    check_out_date: null,
                    gender: null
                }
               this.requestForm={
                        type:null,
                        student_number:null,
                        student_email:null,
                }
                this.type_status=null;


            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            async getRows() {
                const isValid = await this.$refs.formModalValidate.validate();

                if (isValid) {
                    const start = moment(this.datatable.queryParams.filter.check_in_date, "MM/DD/YYYY");
                    const end = moment(this.datatable.queryParams.filter.check_out_date, "MM/DD/YYYY");
                    const timeDistance = end.diff(start, 'days');
                    if (timeDistance < 30) {
                        this.showMessageAlert=true
                        return
                    }

                    this.datatable.showTable = true
                    this.datatable.isLoading = true
                    this.showMessageAlert=false
                    // Date

                    const config = {
                        params: {
                          ...this.datatable.queryParams,
                            type: this.requestForm.bau_type,
                        },
                        paramsSerializer: (params) => qs.stringify(params, {encode: false})
                    };

                    if(['bahcesehir_exchange','besl'].includes(this.requestForm.bau_type)){
                        config.params.student_number=this.requestForm.student_number;
                        config.params.student_email=this.requestForm.student_email;
                    }

                    return HousingReservationFormService.getAll(config)
                        .then((response) => {
                            this.datatable.rows = response.data.data;
                            this.datatable.total = response.data.pagination.total;
                        })
                        .catch((e) => {
                            this.showErrors(e);
                        })
                        .finally(() => {
                            this.datatable.isLoading = false;
                        }
                    );
                }
            },

            // Clear
            formClear() {
                this.formId = null
                this.formProcess = null
                this.$store.dispatch('housingReservationForm/deleteFormData');
            },

            // Update
            updateFormShow(selectedRow) {
                let data = {
                    ...selectedRow,
                    ...this.datatable.queryParams.filter,
                    ...this.requestForm,
                    type_status: this.type_status==='Yes',




                }
                this.formProcess = 'reservationModal';
                this.$store.dispatch('housingReservationForm/saveFormData', data);
                this.selectedReservation = selectedRow;
                delete this.selectedReservation.name
                this.$refs.updateFormModal.$refs.commonModal.show();

            },
            handleVerificationSuccess() {
                this.formProcess = 'reservationModal';
                this.$refs.updateFormModal.$refs.commonModal.hide();
                setGoReservationSystemForm(true)
                this.$router.push('/housing/reservation/form/verified');
            },
        }
    }
</script>
<style scoped>
@media (max-width: 576px) {
    .logo-wrapper img {
        max-width: 70%;
    }

    .datatable-responsive {
        font-size: 14px;
    }

    .datatable-responsive table {
        width: 100%;
        table-layout: auto;
    }

    .box-border-color {
        border-color: red;
    }
}

@media (min-width: 576px) and (max-width: 992px) {
    .datatable-responsive {
        font-size: 16px;
    }
}
</style>
